/**
 * Time to Next Response, measured against a pipeline's target.
 *
 * The clock starts at the oldest customer message nobody has answered yet and
 * stops the moment an agent replies. Time spent waiting on the *customer* is
 * therefore never counted: after an agent replies the clock is not running at
 * all, and it only restarts when the customer writes back.
 *
 * A pipeline change deliberately does NOT reset anything. Elapsed time is
 * derived from the unanswered message, and the target is read from whichever
 * pipeline the ticket is in right now -- so moving a 20-minute-old ticket from
 * Quote (30m) to Customer Support (2h) leaves it showing 20 minutes elapsed
 * with 1h40m left, rather than a fresh 0/120. That falls out of the model
 * instead of needing a rule, which is why it is modelled this way.
 *
 * Historical attribution is a separate concern and does not live here: see
 * `ticket_sla_segments`, which records what each pipeline was measured against
 * at the time so a breach stays reported against the pipeline that incurred it.
 *
 * ## Pauses
 *
 * Business hours answer "was the office open", and that is not the same
 * question as "was anybody able to answer this". An agent at lunch or away for
 * the day is inside opening hours and unable to reply, and counting that hour
 * against them produced targets nobody believed -- which is worse than a loose
 * target, because a number people have learned to discount stops being a
 * number at all.
 *
 * So a ticket's clock also excludes the periods its *assignee* was away. Those
 * periods are an input rather than something this module knows how to find:
 * who owns a ticket, and what they clicked, are database questions, and this
 * file runs in the browser too.
 *
 * An unassigned ticket has no pauses, which is the right answer rather than a
 * gap -- nobody is away from a ticket that is nobody's.
 */
import { addBusinessMinutes, businessMinutesBetween, isOpenAt, } from "./calendar.js";
/**
 * How close to its target a ticket has to be to count as "at risk".
 *
 * Distinct from the `warning` band above, which is a proportion of the
 * allowance: this is an absolute number of business minutes, and it is what
 * the queue's at-risk filter and the dashboard's at-risk tile both count on.
 *
 * It lives here because those two are in different apps and each previously
 * declared its own copy, with a comment saying it matched the other -- a
 * promise nothing could enforce, and one that would fail silently by showing
 * two different answers to the same question.
 */
export const AT_RISK_MINUTES = 15;
/** Nothing to measure. Shared constant so callers compare cheaply. */
export const NO_SLA = {
    level: null,
    targetMinutes: null,
    elapsedMinutes: null,
    remainingMinutes: null,
    consumed: null,
    dueAt: null,
    running: false,
    paused: false,
    pausedReason: null,
    pausedMinutes: 0,
};
/** Resolve a ticket's live SLA position. */
export function slaSnapshot({ clockStartedAt, targetMinutes, now, calendar, pauses = [], }) {
    // Finance, RMAs and Other have no target, and an unassigned pipeline has no
    // target either. Both are "not measured" rather than "instantly compliant".
    if (!clockStartedAt || targetMinutes === null || targetMinutes <= 0) {
        return { ...NO_SLA, running: Boolean(clockStartedAt) };
    }
    const excluded = pausedBusinessMinutes(pauses, clockStartedAt, now, calendar);
    /*
     * Floored at zero. The two terms are measured over the same span so the
     * subtraction cannot really go negative, but a caller handing in a pause
     * that starts before the clock did should get "no time elapsed" rather than
     * a ticket that reads as owing the customer time.
     */
    const elapsed = Math.max(0, businessMinutesBetween(clockStartedAt, now, calendar) - excluded);
    const remaining = targetMinutes - elapsed;
    const away = isPausedAt(pauses, now);
    return {
        level: levelFor(remaining, targetMinutes),
        targetMinutes,
        elapsedMinutes: elapsed,
        remainingMinutes: remaining,
        consumed: Math.min(1, Math.max(0, elapsed / targetMinutes)),
        dueAt: dueAt(clockStartedAt, targetMinutes, pauses, calendar).toISOString(),
        running: true,
        paused: away || !isOpenAt(now, calendar),
        pausedReason: away ? "away" : isOpenAt(now, calendar) ? null : "out_of_hours",
        pausedMinutes: excluded,
    };
}
function levelFor(remainingMinutes, targetMinutes) {
    if (remainingMinutes <= 0)
        return "breached";
    if (remainingMinutes <= targetMinutes / 2)
        return "warning";
    return "ok";
}
/**
 * Business minutes a finished response cycle took. Used when closing an SLA
 * segment, where "now" is the reply rather than the current time.
 *
 * Pauses are excluded here too, and have to be: the recorded segment is what
 * the dashboard reports, and a history that contradicts the clock agents
 * watched all day is the same bug told after the fact.
 */
export function responseMinutes(clockStartedAt, respondedAt, calendar, pauses = []) {
    return Math.max(0, businessMinutesBetween(clockStartedAt, respondedAt, calendar) -
        pausedBusinessMinutes(pauses, clockStartedAt, respondedAt, calendar));
}
// ─────────────────────────────────────────────────────────────────────────────
// Pause arithmetic.
// ─────────────────────────────────────────────────────────────────────────────
/** Whether an instant falls inside any pause. */
export function isPausedAt(pauses, instant) {
    return pauses.some((pause) => instant >= pause.start && instant < pause.end);
}
/**
 * Business minutes inside `[from, to)` that any pause covers.
 *
 * Clipped to the span and merged before measuring, because overlapping pauses
 * are expected rather than exceptional -- somebody who books the day off and
 * then takes their lunch has two rows covering the same hour, and summing them
 * separately would subtract that hour twice and hand the desk an SLA it can
 * never breach.
 */
export function pausedBusinessMinutes(pauses, from, to, calendar) {
    let total = 0;
    for (const pause of mergeClipped(pauses, from, to)) {
        total += businessMinutesBetween(pause.start, pause.end, calendar);
    }
    return total;
}
/** Pauses clipped to `[from, to)`, sorted, and with overlaps coalesced. */
function mergeClipped(pauses, from, to) {
    const clipped = pauses
        .map((pause) => ({
        start: pause.start > from ? pause.start : from,
        end: pause.end < to ? pause.end : to,
    }))
        .filter((pause) => pause.end > pause.start)
        .sort((a, b) => a.start.getTime() - b.start.getTime());
    const merged = [];
    for (const pause of clipped) {
        const last = merged[merged.length - 1];
        // `<=` rather than `<`: two pauses that meet exactly are one pause, and
        // leaving them separate is harmless here but noisier to read back.
        if (last && pause.start <= last.end) {
            if (pause.end > last.end)
                last.end = pause.end;
        }
        else {
            merged.push({ ...pause });
        }
    }
    return merged;
}
/**
 * The instant the allowance runs out, with pauses pushing it later.
 *
 * Solved by iteration rather than in one step because the two depend on each
 * other: how much paused time to add back depends on where the deadline lands,
 * and where it lands depends on how much was added. Each pass can only move
 * the deadline later and only a finite amount of pause exists to absorb, so
 * this converges -- in practice on the second pass, since a clock spanning
 * more than a couple of pauses has breached many times over already.
 */
function dueAt(clockStartedAt, targetMinutes, pauses, calendar) {
    let due = addBusinessMinutes(clockStartedAt, targetMinutes, calendar);
    if (pauses.length === 0)
        return due;
    for (let pass = 0; pass < 8; pass += 1) {
        const excluded = pausedBusinessMinutes(pauses, clockStartedAt, due, calendar);
        const next = addBusinessMinutes(clockStartedAt, targetMinutes + excluded, calendar);
        if (next.getTime() === due.getTime())
            break;
        due = next;
    }
    return due;
}
//# sourceMappingURL=sla.js.map